// Baumumfang.jsx
import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import Papa from 'papaparse';
import {
  ResponsiveContainer,
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend
} from 'recharts';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable'; // ✅ FIX: expliziter Import
import html2canvas from 'html2canvas';
import './wetter.css';
import '../pages/home.css';
import MyOrtMap from './MyOrtMap';
import BfwLogo from '../assets/bfw_logo.png';

/*   UTILS IMPORT */
import { prettyStationName } from '../utils/prettyStationName';

/* ============================ PDF/Styles ============================ */
const PDF = {
  marginPt: 36,
  logo: { maxW: 180, maxH: 58, gap: 18 },
  head: { fillColor: [68, 118, 129], textColor: [255, 255, 255] }, // #447681
};

/* ---------- Mapping ---------- */
const paramMap = {
  baum1:  { label: 'Baum 1\n[mm]',  key: 'Baumumfangsaenderung-Baum1 [mm]',  color: '#4E79A7' },
  baum2:  { label: 'Baum 2 [mm]',  key: 'Baumumfangsaenderung-Baum2 [mm]',  color: '#F28E2B' },
  baum3:  { label: 'Baum 3 [mm]',  key: 'Baumumfangsaenderung-Baum3 [mm]',  color: '#E15759' },
  baum4:  { label: 'Baum 4 [mm]',  key: 'Baumumfangsaenderung-Baum4 [mm]',  color: '#76B7B2' },
  baum5:  { label: 'Baum 5 [mm]',  key: 'Baumumfangsaenderung-Baum5 [mm]',  color: '#59A14F' },
  baum6:  { label: 'Baum 6 [mm]',  key: 'Baumumfangsaenderung-Baum6 [mm]',  color: '#EDC948' },
  baum7:  { label: 'Baum 7 [mm]',  key: 'Baumumfangsaenderung-Baum7 [mm]',  color: '#B07AA1' },
  baum8:  { label: 'Baum 8 [mm]',  key: 'Baumumfangsaenderung-Baum8 [mm]',  color: '#FF9DA7' },
  baum9:  { label: 'Baum 9 [mm]',  key: 'Baumumfangsaenderung-Baum9 [mm]',  color: '#9C755F' },
  baum10: { label: 'Baum 10 [mm]', key: 'Baumumfangsaenderung-Baum10 [mm]', color: '#BAB0AC' }
};

/* ---------- Helpers ---------- */
const removeBOM = (s) => String(s ?? '').replace(/^\uFEFF/, '');

const toNumber = (v) => {
  if (v == null || v === '') return null;
  let s = String(v).trim()
    .replace(/\u2212/g, '-')
    .replace(/\s+/g, '');
  s = s.replace(',', '.');
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
};

const parseDate = (d) => {
  if (!d) return null;
  const s = String(d).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return new Date(s);
  const m = s.match(/^(\d{1,2})\.(\d{1,2})\.(\d{2,4})$/);
  if (m) {
    const dd = m[1].padStart(2, '0');
    const mm = m[2].padStart(2, '0');
    const yyyy = m[3].length === 2 ? `20${m[3]}` : m[3];
    return new Date(`${yyyy}-${mm}-${dd}T00:00:00`);
  }
  const t = new Date(s);
  return Number.isNaN(+t) ? null : t;
};

const formatAT = (d) => (d ? new Date(d).toLocaleDateString('de-AT') : '');

const fmtVal = (v) => {
  if (v == null || v === '') return '–';
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v);
  return String(n).replace('.', ',');
};

function fitContain(imgW, imgH, maxW, maxH) {
  const ratio = imgW / imgH;
  let w = maxW; let h = w / ratio;
  if (h > maxH) { h = maxH; w = h * ratio; }
  return { w, h };
}

const snapshotNodeToCanvas = async (node, desktopWidthPx = 1200) => {
  if (!node) return null;
  const clone = node.cloneNode(true);
  Object.assign(clone.style, {
    position: 'fixed', left: '-10000px', top: '0px',
    width: `${desktopWidthPx}px`, height: 'auto',
    pointerEvents: 'none', opacity: '1', transform: 'none', zIndex: '-1'
  });
  document.body.appendChild(clone);
  await new Promise((r) => requestAnimationFrame(r));
  const canvas = await html2canvas(clone, {
    scale: 2, useCORS: true, backgroundColor: '#ffffff', windowWidth: desktopWidthPx,
  });
  document.body.removeChild(clone);
  return canvas;
};

const addDomImageMobileSafe = async ({
  pdf, node, label, nextYRef, pageW, pageH, margin, contentW,
  desktopWidthPx = 1200, widthFactor = 0.9,
}) => {
  if (!node) return;
  const canvas = await snapshotNodeToCanvas(node, desktopWidthPx);
  if (!canvas) return;
  const imgW = contentW * widthFactor;
  const naturalH = (canvas.height * imgW) / canvas.width;
  const availH = pageH - nextYRef.value - margin;
  if (naturalH > availH) { pdf.addPage(); nextYRef.value = margin; }
  const finalH = Math.min(naturalH, pageH - nextYRef.value - margin);
  const xCenter = margin + (contentW - imgW) / 2;
  if (label) { pdf.setFontSize(12); pdf.text(label, xCenter, nextYRef.value - 6); }
  pdf.addImage(canvas.toDataURL('image/png'), 'PNG', xCenter, nextYRef.value, imgW, finalH);
  nextYRef.value += finalH + 18;
};

async function loadImageAsDataUrl(src) {
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.src = src;
  await new Promise((res, rej) => { img.onload = res; img.onerror = rej; });
  const c = document.createElement('canvas');
  c.width = img.naturalWidth; c.height = img.naturalHeight;
  c.getContext('2d').drawImage(img, 0, 0);
  return c.toDataURL('image/png');
}

const chunkArray = (arr, size) => {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
};

/* ============================== Component ============================== */
const Baumumfang = ({ ort }) => {
  const [rawData, setRawData] = useState([]);
  const [selectedParams, setSelectedParams] = useState(Object.keys(paramMap));
  const [pageIndex, setPageIndex] = useState(0);
  const [entriesPerPage, setEntriesPerPage] = useState(100);
  const [usedUrl, setUsedUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const stationName = String(ort).charAt(0).toUpperCase() + String(ort).slice(1);

  const [csvAllRows, setCsvAllRows] = useState([]);
  const [csvAllHeaders, setCsvAllHeaders] = useState([]);

  const mapRef = useRef(null);
  const chartRef = useRef(null);

  const [logoDataUrl, setLogoDataUrl] = useState(null);
  useEffect(() => { loadImageAsDataUrl(BfwLogo).then(setLogoDataUrl).catch(()=>{}); }, []);

  const filenameVariants = (ortRaw) => {
    const base = String(ortRaw || '').trim();
    if (!base) return [];
    const norm = base
      .normalize('NFKD').replace(/[\u0300-\u036f]/g, '')
      .replace(/[Ää]/g, 'ae').replace(/[Öö]/g, 'oe').replace(/[Üü]/g, 'ue').replace(/ß/g, 'ss')
      .trim();
    const lower = norm.toLowerCase();
    return Array.from(new Set([
      `${lower}.csv`,
      `${lower.replace(/\s+/g, '')}.csv`,
      `${lower.replace(/\s+/g, '-')}.csv`,
      `${lower.replace(/\s+/g, '_')}.csv`,
      `${norm.replace(/\b\w/g, m => m.toUpperCase()).replace(/\s+/g, '')}.csv`,
    ]));
  };

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setErrorMsg('');
        setUsedUrl('');
        setRawData([]);
        setCsvAllRows([]);
        setCsvAllHeaders([]);
        setPageIndex(0);

        const base = 'https://geo.bfw.ac.at/downloads/waldoekodaten/stammumfang';
        const candidates = filenameVariants(ort).map((f) => `${base}/${f}`);
        if (candidates.length === 0) throw new Error('Ungültiger Ort.');

        let csvText = null;
        let urlUsedLocal = '';
        let lastErr = null;

        for (const url of candidates) {
          try {
            const res = await fetch(url, { mode: 'cors', cache: 'no-store', credentials: 'omit' });
            if (!res.ok) throw new Error(`HTTP ${res.status} ${res.statusText}`);
            csvText = removeBOM(await res.text());
            urlUsedLocal = url;
            break;
          } catch (e) { lastErr = e; }
        }
        if (!csvText) throw lastErr || new Error('CSV nicht gefunden');

        const parsed = Papa.parse(csvText, { header: true, skipEmptyLines: true });
        const data = parsed.data || [];
        const headers = (parsed.meta?.fields || []).filter(Boolean);
        setCsvAllHeaders(headers.length ? headers : Object.keys(data?.[0] || {}));
        setCsvAllRows(Array.isArray(data) ? data : []);

        const cleaned = data
          .map((row) => {
            const d = row['Datum'] ?? row['datum'] ?? row['DATE'] ?? row['Date'];
            const parsedD = parseDate(d);
            if (!parsedD) return null;

            const out = { datum: parsedD.toISOString().slice(0, 10) };
            for (const [key, { key: csvKey }] of Object.entries(paramMap)) {
              const num = toNumber(row[csvKey]);
              out[key] = num == null ? null : Number(num.toFixed(2));
            }
            return out;
          })
          .filter(Boolean)
          .sort((a, b) => parseDate(a.datum) - parseDate(b.datum));

        setRawData(cleaned);
        setUsedUrl(urlUsedLocal);
      } catch (err) {
        console.error('Fehler beim CSV-Import:', err);
        setErrorMsg(String(err?.message || err));
        setRawData([]);
      } finally { setLoading(false); }
    };

    load();
  }, [ort]);

  const handleParamClick = (keys) => setSelectedParams(keys);

  const pageCount = useMemo(
    () => (entriesPerPage === 'ALL' ? 1 : Math.ceil(rawData.length / Number(entriesPerPage || 1))),
    [rawData.length, entriesPerPage]
  );

  const displayedData = useMemo(() => {
    const size = entriesPerPage === 'ALL' ? rawData.length : Number(entriesPerPage);
    const start = pageIndex * size;
    return rawData.slice(start, start + size);
  }, [rawData, pageIndex, entriesPerPage]);

  /* ========== PDF Download (Report) ========== */
  const handleReportPdf = useCallback(async () => {
    if (!rawData.length) return;

    const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const margin = PDF.marginPt;
    const contentW = pageW - margin * 2;

    let titleTop = margin;
    if (logoDataUrl) {
      const props = pdf.getImageProperties(logoDataUrl);
      const { w, h } = fitContain(props.width, props.height, PDF.logo.maxW, PDF.logo.maxH);
      pdf.addImage(logoDataUrl, 'PNG', margin, margin - 2, w, h);
      titleTop = margin - 2 + h + PDF.logo.gap;
    }

    pdf.setFontSize(16);
    pdf.text(`Baumumfang – ${stationName}`, margin, titleTop);
    pdf.setFontSize(11);
    const meta = `Parameter: ${selectedParams.map(k => paramMap[k]?.label || k).join(', ') || '—'}`;
    pdf.text(meta, margin, titleTop + 18);

    const nextYRef = { value: titleTop + 36 };

    await addDomImageMobileSafe({
      pdf, node: mapRef.current,
      label: '',
      nextYRef, pageW, pageH, margin, contentW, desktopWidthPx: 1200, widthFactor: 1.15,
    });

    await addDomImageMobileSafe({
      pdf, node: chartRef.current,
      label: 'Grafische Darstellung – Diagramm',
      nextYRef, pageW, pageH, margin, contentW, desktopWidthPx: 1200, widthFactor: 0.95,
    });

    const columns = [
      { header: 'Datum', dataKey: 'datum' },
      ...selectedParams.map(k => ({ header: paramMap[k]?.label || k, dataKey: k })),
    ];

    const body = rawData.map(r => {
      const row = { datum: formatAT(r.datum) };
      selectedParams.forEach(k => { row[k] = r[k] == null ? '' : r[k]; });
      return row;
    });

    if (nextYRef.value > pageH - 120) { pdf.addPage(); nextYRef.value = margin; }

    // ✅ FIX: autoTable(pdf, ...)
    autoTable(pdf, {
      columns,
      body,
      startY: nextYRef.value,
      theme: 'grid',
      styles: { fontSize: 8, cellPadding: 3, overflow: 'linebreak', halign: 'left' },
      headStyles: { ...PDF.head, fontStyle: 'bold', halign: 'left' },
      columnStyles: { datum: { halign: 'left' } },
      margin: { left: margin, right: margin },
      didParseCell: (data) => {
        if (data.column.dataKey !== 'datum' && data.cell.raw !== '' && data.cell.raw != null) {
          data.cell.text = [String(data.cell.raw)];
        }
      },
      didDrawPage: function () {
        const pageCount = pdf.internal.getNumberOfPages();
        const { pageNumber } = pdf.internal.getCurrentPageInfo();
        pdf.setFontSize(9);
        pdf.text(`Seite ${pageNumber} / ${pageCount}`, pageW - margin - 90, pageH - margin / 2);
      },
    });

    pdf.save(`Baumumfang_Report_${ort}.pdf`);
  }, [logoDataUrl, rawData, selectedParams, ort, stationName]);

  /* ========== CSV → PDF (ALLE Spalten/Zeilen) ========== */
  const handleCsvToPdfAll = useCallback(() => {
    if (!csvAllRows.length || !csvAllHeaders.length) return;

    const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const margin = PDF.marginPt;

    let startY = margin;
    if (logoDataUrl) {
      const props = pdf.getImageProperties(logoDataUrl);
      const { w, h } = fitContain(props.width, props.height, 120, 36);
      pdf.addImage(logoDataUrl, 'PNG', margin, margin - 2, w, h);
      startY = margin - 2 + h + 12;
    }

    pdf.setFontSize(14);
    pdf.text(`CSV Export – Baumumfang ${prettyStationName(ort)}`, margin, startY);
    startY += 12;

    pdf.setFontSize(10);
    pdf.text(`Station: ${prettyStationName(ort)}`, margin, startY);
    startY += 12;

    const headerBlocks = chunkArray(csvAllHeaders, 10);

    headerBlocks.forEach((hdrs, idx) => {
      const cols = hdrs.map((h) => ({ header: h, dataKey: h }));
      const body = csvAllRows.map((row) => {
        const o = {};
        hdrs.forEach((h) => { o[h] = row?.[h] ?? ''; });
        return o;
      });

      if (idx > 0) {
        pdf.addPage();
        startY = margin;
      }

      // ✅ FIX: autoTable(pdf, ...)
      autoTable(pdf, {
        columns: cols,
        body,
        startY,
        theme: 'grid',
        styles: { fontSize: 7, cellPadding: 2, overflow: 'linebreak', halign: 'left' },
        headStyles: { ...PDF.head, fontStyle: 'bold', halign: 'left' },
        margin: { left: margin, right: margin },
        didDrawPage: function () {
          const pageCount = pdf.internal.getNumberOfPages();
          const { pageNumber } = pdf.internal.getCurrentPageInfo();
          pdf.setFontSize(9);
          pdf.text(`Seite ${pageNumber} / ${pageCount}`, pageW - margin - 90, pageH - margin / 2);
        },
      });
    });

    pdf.save(`Baumumfang_CSV_alle_${ort || 'daten'}.pdf`);
  }, [csvAllRows, csvAllHeaders, logoDataUrl, ort]);

  /* -------------------------------------- RENDER -------------------------------------- */
  return (
    <div className="wetter-container" id="pdfContent">
      <h2><strong>{prettyStationName(ort)}</strong></h2>

      <div ref={mapRef} className="home-map">
        <MyOrtMap ort={ort} />
      </div>

      <div className="button-container">
        <h2 onClick={() => handleParamClick(Object.keys(paramMap))} style={{ cursor: 'pointer' }}>
          Baumumfangsänderung Zusammenschau
        </h2>
      </div>

      <div className="home-text">
        <p>
          Die Messstation <strong>{prettyStationName(ort)}</strong> liefert täglich aktualisierte Daten zu Baumumfangsänderungen.
        </p>
      </div>

      <br />

      <h3>Grafische Darstellung</h3>
      {!!errorMsg && <div className="error-banner"><h4>Die Daten sind heute nicht vorhanden.</h4></div>}

      <div ref={chartRef} className="linechart-div neat-chart">
        <ResponsiveContainer width="100%" height={320}>
          <LineChart data={rawData} margin={{ top: 10, right: 16, bottom: 0, left: 0 }}>
            <CartesianGrid strokeDasharray="4 4" opacity={0.5} />
            <XAxis dataKey="datum" tick={{ fontSize: 11 }} tickFormatter={formatAT} tickMargin={8} minTickGap={24} axisLine={false} tickLine={false} />
            <YAxis width={56} tick={{ fontSize: 11 }} tickFormatter={(v) => (v == null ? '' : fmtVal(v))} axisLine={false} tickLine={false} />
            <Tooltip
              labelFormatter={(d) => `Datum: ${formatAT(d)}`}
              formatter={(value, name) => [fmtVal(value), paramMap[name]?.label || name]}
              wrapperStyle={{ borderRadius: 8, boxShadow: '0 6px 24px rgba(0,0,0,.08)' }}
            />
            <Legend verticalAlign="bottom" height={32} wrapperStyle={{ paddingTop: 18 }} iconType="plainline" />
            {selectedParams.map((key) => {
              const p = paramMap[key];
              if (!p) return null;
              return (
                <Line
                  key={key}
                  type="monotone"
                  dataKey={key}
                  name={p.label}
                  stroke={p.color}
                  strokeWidth={2}
                  dot={false}
                  connectNulls
                  isAnimationActive={false}
                />
              );
            })}
          </LineChart>
        </ResponsiveContainer>
      </div>

      <br /><br /><br />

      {usedUrl && (
        <div>
          <a className="param-button active-button" href={usedUrl} target="_blank" rel="noreferrer" style={{ marginBottom: 0, marginRight: 8, textDecoration: 'none' }}>
            CSV Download
          </a>

          <button onClick={handleCsvToPdfAll} className="param-button active-button" disabled={loading || !csvAllRows.length} style={{ marginLeft: 8 }}>
            CSV → PDF Download
          </button>
        </div>
      )}

      <br />

      <h3>Messwerte</h3>

      <div className="entries-dropdown">
        <label>
          <strong>Einträge pro Seite:</strong>{' '}
          <select
            value={entriesPerPage === 'ALL' || entriesPerPage >= rawData.length ? 'ALL' : entriesPerPage}
            onChange={(e) => {
              const val = e.target.value === 'ALL' ? 'ALL' : parseInt(e.target.value, 10);
              setEntriesPerPage(val);
              setPageIndex(0);
            }}
            disabled={loading}
          >
            <option value={100}>100</option>
            <option value={200}>200</option>
            <option value="ALL">ALLE</option>
          </select>
        </label>
      </div>

      <div className="table-wrapper">
        <table className="data-table responsive-table">
          <colgroup>
            <col style={{ width: 25 }} />
            <col style={{ width: 85 }} />
            {selectedParams.map((_, i) => <col key={i} />)}
          </colgroup>
          <thead>
            <tr>
              <th style={{ width: 10 }}>Nr.</th>
              <th style={{ width: 10 }}>Datum</th>
              {selectedParams.map((key) => (
                <th style={{ width: 100 }} key={key} className="th-right">
                  {paramMap[key]?.label || key}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {displayedData.map((row, idx) => (
              <tr key={idx}>
                <td className="td-index">
                  {(pageIndex * (entriesPerPage === 'ALL' ? rawData.length : Number(entriesPerPage))) + idx + 1}
                </td>
                <td className="td-date">{formatAT(row.datum)}</td>
                {selectedParams.map((key) => (
                  <td key={key} className="td-number">{fmtVal(row[key])}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="page-buttons">
        {Array.from({ length: pageCount }).map((_, i) => (
          <button
            key={i}
            onClick={() => setPageIndex(i)}
            className={pageIndex === i ? 'active' : ''}
            disabled={loading}
            aria-label={`Seite ${i + 1}`}
          >
            {i + 1}
          </button>
        ))}
      </div>

      <p>
        <small>
          * „PDF Download“ erzeugt einen Report (Logo, Karte, Diagramm & Tabelle mit gewählten Bäumen).<br />
          ** „CSV → PDF Download“ exportiert die komplette, ungefilterte CSV mit allen Spalten.
        </small>
      </p>

      <br />

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button
          onClick={handleReportPdf}
          className="param-button"
          disabled={loading || rawData.length === 0}
          title="Report: Logo → Metadaten → Karte → Diagramm → Tabelle (nur gewählte Kurven)"
        >
          PDF Download
        </button>
      </div>
    </div>
  );
};

export default Baumumfang;
